import React, { useEffect, useRef, useState } from 'react';
import { 
  FaTrophy, FaCertificate, FaBook, FaProjectDiagram, FaGraduationCap,
  FaAward, FaMedal, FaStar, FaRocket, FaLightbulb, FaCrown, FaUsers,
  FaChartLine, FaCloud, FaCode, FaShieldAlt, FaBrain, FaHandsHelping,
  FaSun, FaMoon
} from 'react-icons/fa';
import { SiAws, SiKubernetes, SiTerraform, SiHackerrank } from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';

const Achievements = () => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeAchievement, setActiveAchievement] = useState(null);
  const [pulseIndex, setPulseIndex] = useState(0);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate-achievement-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Auto pulse through achievements
    const interval = setInterval(() => {
      setPulseIndex((prev) => (prev + 1) % achievements.length);
    }, 3000);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      clearInterval(interval);
    };
  }, []);

  const achievements = [
    {
      icon: <FaCertificate className="h-10 w-10" />,
      title: "IIM-B Vista 2019",
      description: "Selected as the youngest participant in prestigious business conclave",
      details: "Certified participation in India's premier business school leadership program",
      year: "2019",
      color: "from-blue-600 to-cyan-500",
      darkColor: "from-blue-500 to-cyan-400",
      pulse: false
    },
    {
      icon: <FaTrophy className="h-10 w-10" />,
      title: "Academic Excellence",
      description: "Consistently ranked among top 5% students at university level",
      details: "Maintained outstanding academic performance throughout engineering",
      year: "2020-2023",
      color: "from-yellow-500 to-orange-500",
      darkColor: "from-yellow-400 to-orange-400",
      pulse: true
    },
    {
      icon: <FaBook className="h-10 w-10" />,
      title: "Continuous Learning",
      description: "Daily practice with AWS, Kubernetes, and Terraform labs",
      details: "500+ hours of hands-on cloud infrastructure and DevOps practice",
      year: "Ongoing",
      color: "from-green-500 to-emerald-500",
      darkColor: "from-green-400 to-emerald-400",
      pulse: true
    },
    {
      icon: <FaProjectDiagram className="h-10 w-10" />,
      title: "Project Excellence",
      description: "Well-documented DevOps projects with detailed architecture",
      details: "Professional documentation and architecture diagrams for all projects",
      year: "2023-2024",
      color: "from-purple-500 to-pink-500",
      darkColor: "from-purple-400 to-pink-400",
      pulse: true
    },
    {
      icon: <SiHackerrank className="h-10 w-10" />,
      title: "AWS Hands-on",
      description: "Extensive practical experience with AWS services",
      details: "Implemented production-grade solutions using 15+ AWS services",
      year: "2023-Present",
      color: "from-orange-500 to-yellow-500",
      darkColor: "from-orange-400 to-yellow-400",
      pulse: true
    },
    {
      icon: <SiKubernetes className="h-10 w-10" />,
      title: "Kubernetes Mastery",
      description: "Production experience with Kubernetes orchestration",
      details: "Deployed and managed scalable applications on Kubernetes clusters",
      year: "2023-Present",
      color: "from-blue-500 to-cyan-500",
      darkColor: "from-blue-400 to-cyan-400",
      pulse: true
    },
    {
      icon: <SiTerraform className="h-10 w-10" />,
      title: "Infrastructure as Code",
      description: "Expertise in Terraform for cloud provisioning",
      details: "Infrastructure automation using Terraform modules and best practices",
      year: "2023-Present",
      color: "from-purple-500 to-indigo-500",
      darkColor: "from-purple-400 to-indigo-400",
      pulse: true
    },
    {
      icon: <FaAward className="h-10 w-10" />,
      title: "Technical Leadership",
      description: "Mentored peers in DevOps practices and tools",
      details: "Guided team members in implementing DevOps best practices",
      year: "2023-Present",
      color: "from-red-500 to-pink-500",
      darkColor: "from-red-400 to-pink-400",
      pulse: false
    }
  ];

  const certifications = [
    { name: "AWS Cloud Practitioner", status: "In Progress", icon: <FaAward />, bgColor: "from-orange-100 to-yellow-100 dark:from-orange-900/30 dark:to-yellow-900/30" },
    { name: "Kubernetes Administrator", status: "In Progress", icon: <SiKubernetes />, bgColor: "from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30" },
    { name: "Terraform Associate", status: "Planned", icon: <SiTerraform />, bgColor: "from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30" },
    { name: "HackerRank Problem Solving", status: "Achieved", icon: <SiHackerrank />, bgColor: "from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30" }
  ];

  const milestones = [
    { number: "50+", label: "Labs Completed", icon: "📚", color: "from-blue-500 to-cyan-500" },
    { number: "10+", label: "Projects Deployed", icon: "🚀", color: "from-purple-500 to-pink-500" },
    { number: "500+", label: "Hours Practice", icon: "⏰", color: "from-green-500 to-emerald-500" },
    { number: "100%", label: "Code Quality", icon: "⭐", color: "from-yellow-500 to-orange-500" }
  ];

  return (
    <section 
      id="achievements" 
      ref={containerRef}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      Theme Toggle Button
      <button
        onClick={toggleDarkMode}
        className="fixed top-8 right-8 group z-50"
        aria-label="Toggle theme"
      >
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 rounded-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-800 to-gray-900' 
              : 'bg-gradient-to-r from-white to-gray-50'
          } shadow-lg transition-all duration-500`}></div>
          <div className="relative w-full h-full flex items-center justify-center">
            <FaSun className={`absolute w-6 h-6 text-yellow-500 transition-all duration-500 ${
              darkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
            }`} />
            <FaMoon className={`absolute w-6 h-6 text-blue-400 transition-all duration-500 ${
              darkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
            }`} />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-300"></div>
        </div>
      </button>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-0 w-full h-48 blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-600/5 via-cyan-500/5 to-purple-600/5' 
            : 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10'
        }`}></div>
        <div className={`absolute bottom-0 right-0 w-full h-48 blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-yellow-500/5 via-orange-500/5 to-red-500/5' 
            : 'bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10'
        }`}></div>
        
        {/* Floating stars */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full animate-twinkle ${
              darkMode ? 'bg-yellow-400/40' : 'bg-yellow-400/50'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          ></div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-500 shadow-xl">
              <FaTrophy className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-white text-sm">🏆</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Achievements & </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Milestones
            </span>
          </h2>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-800/50 text-cyan-300' 
              : 'bg-gradient-to-r from-blue-100/80 to-cyan-100/80 border-blue-200/50 text-blue-600'
          }`}>
            <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full animate-ping"></div>
            <TypeAnimation
              sequence={[
                'Certifications & Awards',
                2000,
                'Academic Excellence',
                2000,
                'Continuous Learning',
                2000,
                'Professional Growth',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="font-semibold"
            />
          </div>
        </div>

        {/* Milestones Stats */}
        <div className="mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className={`backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border animate-slide-up ${
                  darkMode 
                    ? 'bg-gray-800/50 border-gray-700' 
                    : 'bg-white/90 border-gray-100'
                }`}
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)'
                }}
              >
                <div className="text-3xl mb-2 text-center">{milestone.icon}</div>
                <div className={`text-3xl md:text-4xl font-bold text-center mb-2 bg-gradient-to-r ${milestone.color} bg-clip-text text-transparent`}>
                  {milestone.number}
                </div>
                <div className={`text-sm text-center ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>{milestone.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements Grid */}
        <div className="mb-16">
          <h3 className={`text-2xl md:text-3xl font-bold text-center mb-8 ${
            darkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Key <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Achievements</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                onMouseEnter={() => setActiveAchievement(index)}
                onMouseLeave={() => setActiveAchievement(null)}
                className={`group relative overflow-hidden rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border animate-slide-up ${
                  darkMode 
                    ? 'bg-gray-800/50 border-gray-700' 
                    : 'border-gray-100'
                } ${index === pulseIndex ? 'animate-pulse-slow' : ''}`}
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)'
                }}
              >
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  darkMode ? achievement.darkColor : achievement.color
                } opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                
                {/* Glow effect for pulsing */}
                {achievement.pulse && (
                  <div className="absolute inset-0 bg-gradient-to-br from-current to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
                )}
                
                {/* Year badge */}
                <div className="absolute top-4 right-4">
                  <div className={`px-2 py-1 backdrop-blur-sm rounded-full text-xs font-medium ${
                    darkMode 
                      ? 'bg-gray-800/80 text-gray-300' 
                      : 'bg-white/80 text-gray-700'
                  }`}>
                    {achievement.year}
                  </div>
                </div>
                
                {/* Icon */}
                <div className={`mb-4 inline-flex p-3 rounded-xl bg-gradient-to-br ${
                  darkMode ? achievement.darkColor : achievement.color
                } text-white transform group-hover:scale-110 transition-transform duration-500`}>
                  {achievement.icon}
                </div>
                
                {/* Content */}
                <h4 className={`text-xl font-bold mb-2 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{achievement.title}</h4>
                <p className={`mb-3 ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>{achievement.description}</p>
                <div className={`text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                  darkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  {achievement.details}
                </div>
                
                {/* Hover line */}
                <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${
                  darkMode ? achievement.darkColor : achievement.color
                } transition-all duration-500 group-hover:w-full`}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications Section */}
        <div className="mb-16">
          <div className={`relative overflow-hidden rounded-2xl border p-8 ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border-gray-700/30' 
              : 'bg-gradient-to-r from-blue-50/80 to-cyan-50/80 border-blue-200/50'
          }`}>
            <div className={`absolute top-0 right-0 w-48 h-48 rounded-full blur-2xl ${
              darkMode 
                ? 'bg-gradient-to-br from-blue-500/10 to-cyan-400/10' 
                : 'bg-gradient-to-br from-blue-500/20 to-cyan-400/20'
            }`}></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
                  <FaCertificate className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className={`text-2xl font-bold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Certifications Journey</h3>
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                    Professional certifications in progress and planned
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {certifications.map((cert, index) => (
                  <div
                    key={index}
                    className={`backdrop-blur-sm rounded-xl p-4 border hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${
                      darkMode 
                        ? 'bg-gray-800/50 border-gray-700' 
                        : 'bg-white/80 border-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg bg-gradient-to-r ${cert.bgColor}`}>
                        <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          {cert.icon}
                        </div>
                      </div>
                      <span className={`font-medium ${
                        darkMode ? 'text-gray-300' : 'text-gray-900'
                      }`}>{cert.name}</span>
                    </div>
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      cert.status === 'Achieved' 
                        ? `${
                            darkMode 
                              ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 text-green-300' 
                              : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
                          }`
                        : cert.status === 'In Progress'
                        ? `${
                            darkMode 
                              ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 text-blue-300' 
                              : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700'
                          }`
                        : `${
                            darkMode 
                              ? 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 text-yellow-300' 
                              : 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700'
                          }`
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        cert.status === 'Achieved' ? 'bg-green-500' :
                        cert.status === 'In Progress' ? 'bg-blue-500 animate-pulse' :
                        'bg-yellow-500'
                      }`}></div>
                      {cert.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Cloud Practice Banner */}
        <div className="relative overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-600 opacity-90"></div>
          
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 left-4 animate-float">
              <FaCloud className="h-8 w-8 text-white" />
            </div>
            <div className="absolute top-1/2 right-8 animate-float" style={{ animationDelay: '1s' }}>
              <FaCode className="h-6 w-6 text-white" />
            </div>
            <div className="absolute bottom-8 left-1/4 animate-float" style={{ animationDelay: '2s' }}>
              <FaShieldAlt className="h-7 w-7 text-white" />
            </div>
          </div>
          
          <div className="relative z-10 p-8 md:p-10 text-white">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <FaBrain className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-2">Continuous Cloud Practice</h3>
                    <p className="text-blue-100 text-lg">
                      Regular hands-on practice with AWS, Kubernetes, and Terraform to strengthen 
                      cloud infrastructure skills and stay updated with latest technologies.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4 mt-6">
                  {[
                    { label: "Daily Labs", value: "2-3 hours" },
                    { label: "Weekly Projects", value: "1-2 projects" },
                    { label: "Monthly Goals", value: "New skill acquired" },
                    { label: "Quarterly Review", value: "Skills assessment" }
                  ].map((item, index) => (
                    <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                      <div className="text-sm text-blue-200">{item.label}</div>
                      <div className="font-bold">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-center">
                <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FaGraduationCap className="h-12 w-12" />
                </div>
                <div className="text-2xl font-bold">Lifelong Learning</div>
                <div className="text-blue-200">Always improving</div>
              </div>
            </div>
          </div>
        </div>

        {/* Learning Philosophy */}
        <div className="mt-12 text-center">
          <div className={`inline-flex items-center gap-4 px-6 py-4 rounded-2xl border backdrop-blur-sm ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-gray-700/30' 
              : 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-blue-200/30'
          }`}>
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
              <FaLightbulb className="h-6 w-6 text-white" />
            </div>
            <div className="text-left">
              <p className={`text-lg font-semibold ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>
                "Success is built on consistent practice and continuous learning"
              </p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Committed to daily improvement and staying at the forefront of cloud technology
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes twinkle {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 1;
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
          }
        }
        
        @keyframes achievement-in {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
        
        .animate-twinkle {
          animation: twinkle 2s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s infinite;
        }
        
        .animate-achievement-in {
          animation: achievement-in 0.6s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Achievements;